import React, { useState, type ReactNode } from 'react';

import classnames from 'classnames';

import Itinerary from 'wanderu-component-itinerary';
import A from 'wanderu-component-react-router-link';
import { SplitTest, Variant } from 'wanderu-component-split-test';
import Tags from 'wanderu-component-tags';
import {
    WDSButton,
    Chip,
    ElementQuery,
    TripModes,
    Typography,
    Text,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import CarrierInfo from './CarrierInfo/CarrierInfo';
import CarrierRating from './CarrierRating/CarrierRating';
import DropdownPill from './DropdownPill/DropdownPill';
import FeaturesDropdown from './FeaturesDropdown/FeaturesDropdown';
import Events from './events/SearchResultEvents';
import { getTotalDiscount } from './utils';
import Duration from '../../containers/Duration/Duration';
import Endpoint from '../../containers/Endpoint/Endpoint';
import FareClass from '../../containers/FareClass/FareClass';
import Price from '../../containers/Price/Price';

// we're using styles[tag] for the tag-related classes, which isn't seen by eslint
// eslint-disable-next-line css-modules/no-unused-class
import styles from './SearchResult.scss';
import breakpoints from 'wanderu-css/mediaQueries.scss';

import type { SearchResultProps } from './types';
import type { TFunction } from 'i18next';

const SIZE_QUERY = {
    isSmallScreen: {
        maxWidth: breakpoints.maxWidthSmallScreen,
    },
};

interface SelectButtonProps {
    disabled?: boolean;
    isMobile?: boolean;
    onClick: () => void;
    t: TFunction;
}

const SelectButton: React.FC<SelectButtonProps> = ({
    disabled = false,
    isMobile = false,
    onClick,
    t,
}) => (
    <WDSButton
        aria-label={t('select_searchResult')}
        className={classnames(
            styles['select-button'],
            {
                [styles['mobile-select-button']]: isMobile,
            },
        )}
        color="secondary"
        disabled={disabled}
        onClick={onClick}
    >
        <Typography variant="subtitle">
            {t('select_searchResult')}
        </Typography>
    </WDSButton>
);

const LocalizedSelectButton = localize(SelectButton, ['t'], 'baseApp');

const SearchResult = ({
    arriveIsNearby = false,
    carrierCode,
    carrierName,
    className = '',
    departIsNearby = false,
    disabled = false,
    fareClasses = [],
    features = [],
    fees,
    forceModalClose = false,
    i18nMoney,
    isLuxury = false,
    isNextGen = false,
    isRouted = false,
    liveCheckIds = {
        affiliateId: '',
        allTripIds: [],
        trackingId: '',
    },
    onSelect,
    pageSlug = '',
    reviews,
    seats = 1,
    selectedFare = null,
    showTotalPrice = false,
    t,
    tags = [],
    transfers = 0,
    tripId = '',
    tripModes = [],
    variant = 'default',
    wheelchairs = 0,
}: SearchResultProps) => {
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [tripDetailsVisible, setTripDetailsVisible] = useState(false);

    const handleSelect = () => {
        return onSelect?.(tripId);
    };

    const onChangeScreenSize = (queries: { isSmallScreen: boolean }) => {
        if (queries.isSmallScreen === isSmallScreen) {
            return;
        }

        // see SIZE_QUERY for what gets applied to state via ElementQuery
        setIsSmallScreen(queries.isSmallScreen);
    };

    const onClickAnywhere = () => {
        // click-anywhere-to-select is only enabled on small screens
        if (isSmallScreen) {
            handleSelect();
        }
    };

    /**
         * show/hide/request trip details
         */
    const onToggleTripDetails = (e: React.MouseEvent) => {
        e.stopPropagation();

        const action = tripDetailsVisible
            ? 'hide'
            : 'show';
        Events.detailsButtonClicked({
            action,
            tripId,
            variant,
        });

        setTripDetailsVisible(!tripDetailsVisible);
    };

    const onHoverFeatures = () => {
        Events.seeMoreFeaturesHovered({
            tripId,
            variant,
        });
    };

    const onClickFeatures = () => {
        Events.seeMoreFeaturesClicked({
            tripId,
            variant,
        });
    };

    const onCarrierLinkSelect = (e: React.MouseEvent) => {
        if (isSmallScreen && variant !== 'tripSummary') {
            e.preventDefault();
            handleSelect();
        }

        e.stopPropagation();
        Events.carrierLinkSelected({
            carrierName,
            rating: false,
            variant,
        });
    };

    const onCarrierRatingLinkSelect = (e: React.MouseEvent) => {
        if (isSmallScreen && variant !== 'tripSummary') {
            e.preventDefault();
            handleSelect();
        }

        e.stopPropagation();
        Events.carrierLinkSelected({
            carrierName,
            rating: true,
            variant,
        });
    };

    const renderRatingsField = () => {
        return (
            <>
                {reviews?.review_count && reviews?.review_count > 4 && (
                    <CarrierRating
                        rating={reviews?.overall}
                        text={t('star', { count: reviews?.overall })}
                    />
                )}
            </>

        );
    };

    const queryElement = typeof window !== 'undefined'
        ? document.body
        : null;
    const tagObjects = tags?.map((tag) => {
        return {
            className: `${styles.tag} ${styles[tag]}`,
            text: t(`${tag}_tag`),
        };
    });
    const totalDiscount = getTotalDiscount(fees, i18nMoney);
    const showFareClasses = !!fareClasses?.length;
    const Container = ({ children }: { children: ReactNode }) => !isRouted
        ? (
            <A
                className={styles['carrier-link']}
                href={`/carrier/${pageSlug}/`}
                onClick={onCarrierLinkSelect}
                target="_blank"
            >{children}</A>
        )
        : <React.Fragment>{children}</React.Fragment>;

    const isTimeTable = variant === 'timeTable';
    const isTripSummary = variant === 'tripSummary';

    return (
        <ElementQuery
            element={queryElement}
            onChange={onChangeScreenSize}
            passAsProps={false}
            queries={SIZE_QUERY}
        >
            <div
                className={classnames(
                    className,
                    styles.container,
                    {
                        [styles['with-tags']]: !!tags?.length,
                        [styles['no-pointer']]: variant === 'tripSummary',
                    },
                )}
                data-cy={carrierCode}
                data-id={`Select-mobile${isTripSummary
                    ? '-tripSummary'
                    : ''}`}
                onClick={!disabled
                    ? onClickAnywhere
                    : undefined}
            >
                {totalDiscount && carrierCode !== 'MIX'
                    ? <div className={styles.banner}>
                        Wanderu Exclusive: 20% Off
                    </div>
                    : ''}
                {tagObjects && tagObjects.length > 0 && (
                    <Tags
                        className={styles.tags}
                        tags={tagObjects}
                        disabled
                    />
                )}
                <div className={styles['search-result']}>
                    <TripModes
                        className={classnames(
                            styles['trip-modes'],
                        )}
                        modes={tripModes}
                    />
                    <Price
                        className={classnames(
                            styles.price,
                            { [styles['time-table-price']]: isTimeTable },
                        )}
                        seats={seats}
                        selectedFare={selectedFare}
                        showTotalPrice={showTotalPrice}
                        totalDiscount={totalDiscount}
                        tripId={tripId}
                        tripSummary={isTripSummary}
                        wheelchairs={wheelchairs}
                    />
                    <div className={styles['endpoint-container']}>
                        <Endpoint
                            className={styles['depart-endpoint']}
                            duration={(
                                <Duration
                                    className={styles.duration}
                                    tripId={tripId}
                                />
                            )}
                            nearby={departIsNearby}
                            transfers={transfers}
                            tripId={tripId}
                            type="depart"
                        />
                        <Endpoint
                            className={styles['arrive-endpoint']}
                            nearby={arriveIsNearby}
                            tripId={tripId}
                            type="arrive"
                        />
                    </div>
                    {variant === 'default' && (
                        <LocalizedSelectButton
                            disabled={disabled}
                            onClick={handleSelect}
                        />
                    )}
                    <div
                        className={classnames(
                            styles['carrier-info-container'],
                        )}
                    >
                        {!isTimeTable
                            ? (
                                <>
                                    <Container>
                                        <CarrierInfo
                                            carrierCode={carrierCode}
                                            carrierName={carrierName}
                                            className={classnames(
                                                styles['carrier-info'],
                                            )}
                                            isRouted={isRouted}
                                            tripModes={tripModes}
                                        />
                                    </Container>

                                    <A
                                        className={styles['carrier-rating']}
                                        href={`/carrier/${pageSlug}/#carrier-review-section`}
                                        onClick={onCarrierRatingLinkSelect}
                                        target="_blank"
                                        underline="none"
                                        variant="subdued"
                                    >
                                        {renderRatingsField()}
                                    </A>
                                </>
                            )
                            : (
                                <>
                                    <CarrierInfo
                                        carrierCode={carrierCode}
                                        carrierName={carrierName}
                                        className={classnames(
                                            styles['carrier-info'],
                                        )}
                                        isRouted={isRouted}
                                        tripModes={tripModes}
                                    />
                                    <span className={styles['carrier-rating']}>
                                        {renderRatingsField()}
                                    </span>
                                </>
                            )}
                    </div>
                    <div className={styles['details-container']}>
                        {!isTimeTable && (
                            <DropdownPill
                                className={styles.chip}
                                isOpen={tripDetailsVisible}
                                onToggle={onToggleTripDetails}
                                text={t('includes-transfers', {
                                    count: transfers,
                                })}
                            />
                        )}
                        {isLuxury && (
                            <Chip
                                className={`${styles.chip} ${styles['luxury-chip']}`}
                                text={t('luxury')}
                            />
                        )}
                        {isNextGen && (
                            <Chip
                                className={`${styles.chip} ${styles['luxury-chip']}`}
                                text="NextGen"
                            />
                        )}
                    </div>
                    {tripDetailsVisible && (
                        <>
                            <FeaturesDropdown
                                className={styles['carrier-features']}
                                features={features}
                                onClickFeatures={onClickFeatures}
                                onHoverFeatures={onHoverFeatures}
                            />
                            <Itinerary
                                arriveIsNearby={arriveIsNearby}
                                className={classnames(
                                    styles['trip-details-container'],
                                    styles['trip-details']
                                )}
                                departIsNearby={departIsNearby}
                                seats={seats}
                                tripId={tripId}
                                type="search"
                                wheelchairs={wheelchairs}
                            />
                            {variant === 'default' && (
                                <LocalizedSelectButton
                                    disabled={disabled}
                                    onClick={handleSelect}
                                    isMobile
                                />
                            )}
                        </>
                    )}
                </div>
                {carrierCode === 'USOUR' && (
                    <SplitTest testName="ourbusRefundMessage">
                        <Variant name="seesMessage">
                            <div className={styles.bottomBanner}>
                                <Text variant="text-xs">
                                    <A className={styles.carrierLink} href="https://www.ourbus.com/cancellationpolicy" target="_blank">
                                        {t<string>('ourbus-refund-policy')}
                                    </A>
                                    &nbsp; &bull; &nbsp;
                                    {t<string>('ourbus-refund-message')}
                                </Text>
                            </div>
                        </Variant>
                    </SplitTest>
                )}
            </div>
            {showFareClasses && (
                <FareClass
                    fareClasses={fareClasses}
                    forceModalClose={forceModalClose}
                    liveCheckIds={liveCheckIds}
                    seats={seats}
                    tripId={tripId}
                    wheelchairs={wheelchairs}
                />
            )}
        </ElementQuery>
    );
};

export {
    SearchResult,
    LocalizedSelectButton as SelectButton,
};
export default localize(SearchResult, ['i18nMoney', 't'], 'baseApp');
